import React from 'react';
import type { CertificateStatus, Certificate } from 'features/dashboard/types/dashboard';
import { useNavigate } from 'react-router-dom';
import { useSelectedClient } from '../../hooks/useSelectedClient';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Chip } from 'primereact/chip';
import { Paginator } from 'primereact/paginator';
import Skeleton from 'react-loading-skeleton';
import { TabView, TabPanel } from 'primereact/tabview';

import { useMasterCertificates } from '../master-certificate/hooks/useMasterCertificates';
import axios from 'axios';
import { handleAxiosError } from 'utilities/apiUtils';
import CertificateCard from './components/CertificateCard';
import SentCertificatesPanel from './SentCertificatesPanel';
import RecentActivityPanel from './RecentActivityPanel';
import AddCertificate from './components/AddCertificate';
import RequestsList from './components/RequestsList';
import ViewCOIRequest from './components/ViewCOIRequest';
import { useCoiRequests } from './hooks/useCoiRequests';
import { useViewMode } from '../../hooks/useViewMode';
import ErrorState from 'components/ErrorState';
import '../../styles/pages.css';


function InsuredRequestsPanel({ insuredId }: { insuredId: string }) {
  // Only view requests, no approve/decline actions
  const { requests, isLoading, error } = useCoiRequests(true, insuredId, 'pending');
  const [viewId, setViewId] = React.useState<string | null>(null);
  return (
    <>
      {isLoading ? (
        <div style={{ padding: '12px' }}>
          {Array.from({ length: 5 }).map((_, idx) => (
            <div key={idx} style={{ marginBottom: 16, background: '#fff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 2px 8px rgba(44,83,100,0.07)', border: '1px solid #e6e7ef' }}>
              <Skeleton width="30%" height="1.1rem" style={{ marginBottom: 8 }} />
              <Skeleton width="80%" height="0.9rem" style={{ marginBottom: 6 }} />
              <Skeleton width="40%" height="0.8rem" />
            </div>
          ))}
        </div>
      ) : (
        <RequestsList
          items={requests}
          onView={setViewId}
          pageSize={5}
          viewOnly
        />
      )}
      <ViewCOIRequest visible={!!viewId} onHide={() => setViewId(null)} databaseId={viewId} showSendCertificateButton={false} />
    </>
  );
}

export default function InsuredDashboardPage() {
  const { setViewMode } = useViewMode();
  const navigate = useNavigate();
  const [showAddCertificate, setShowAddCertificate] = React.useState(false);
  React.useEffect(() => {
    setViewMode('insured');
  }, [setViewMode]);

  const { hasSelectedClient, insuredId } = useSelectedClient();

  const { certificates: masterCertificates = [], isLoading: isMasterCertLoading, error: masterCertError } = useMasterCertificates(insuredId);
  const [unreadCount, setUnreadCount] = React.useState<number>(0);
  React.useEffect(() => {
    const fetchUnread = async () => {
      try {
        const token = localStorage.getItem('igtoken');
        if (!token) return;
        const res = await axios.get(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/activity-unread-count`, {
          headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
        });
        setUnreadCount(res.data.unreadCount || 0);
      } catch (err) {
        handleAxiosError(err);
        setUnreadCount(0);
      }
    };
    fetchUnread();
  }, []);

  const [certFilter, setCertFilter] = React.useState<'All' | 'Active'>('All');

  const mapMasterToCertificate = (mc: any): Certificate => ({
    id: mc.databaseId,
    title: mc.name,
    lineOfBusiness: mc.policies?.length > 0 ? mc.policies[0].linesOfBusiness?.[0] || '' : '',
    certificateNumber: mc.databaseId,
    issuedDateLabel: mc.createDate ? new Date(mc.createDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '',
    status: mc.active ? 'Active' : 'Expired',
    icon: 'pi pi-file',
  });

  const mappedCertificates = React.useMemo(() => masterCertificates.map(mapMasterToCertificate), [masterCertificates]);

  const filteredCertificates = React.useMemo(() => {
    return certFilter === 'Active' ? mappedCertificates.filter((c) => c.status === 'Active') : mappedCertificates;
  }, [certFilter, mappedCertificates]);

  const [certPageFirst, setCertPageFirst] = React.useState(0);
  const pagedCertificates = React.useMemo(() => {
    return filteredCertificates.slice(certPageFirst, certPageFirst + 5);
  }, [filteredCertificates, certPageFirst]);

  return (
    <div className="page dashboardPage">
      <div className="dashboardMasterCerts" style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', padding: 20, border: '1px solid #e6e7ef' }}>
        <div className="page__sectionHeader">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h1 className="page__title" style={{ color: '#381574', fontWeight: 600, margin: 0 }}>My Certificates</h1>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, background: '#f0ddff', color: '#A200FF', borderRadius: 20, padding: '1px 7px', fontSize: 10, fontWeight: 600, border: '1px solid #e6b3ff' }}>
                <span style={{ color: '#8500D0' }}>{filteredCertificates.filter((c) => c.status === 'Active').length}</span>
                <span style={{ opacity: 0.35, fontSize: 9 }}>/</span>
                <span>{filteredCertificates.length}</span>
              </span>
            </div>
          </div>
          <div className="page__actions" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="page__chips">
              <Chip label="All" className={certFilter === 'All' ? 'page__chip page__chip--active' : 'page__chip'} onClick={() => setCertFilter('All')} />
              <Chip label="Active" className={certFilter === 'Active' ? 'page__chip page__chip--active' : 'page__chip'} onClick={() => setCertFilter('Active')} />
            </div>
            <Button icon="pi pi-plus" label="Request Certificate" className="p-button-sm appButton--primary" onClick={() => setShowAddCertificate(true)} />
          </div>
        </div>
        <div className="dashboardCertGrid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 12, minHeight: 120, alignItems: 'stretch', marginTop: 14 }}>
          {isMasterCertLoading
            ? Array.from({ length: 5 }).map((_, index) => (
                <Card key={index} className="certCard" style={{ minWidth: 0 }}>
                  <div className="certCard__top">
                    <Skeleton width={36} height={36} borderRadius={10} />
                    <Skeleton width={56} height={20} borderRadius={10} />
                  </div>
                  <div className="certCard__title">
                    <Skeleton width="90%" height={12} />
                    <div style={{ marginTop: 6 }}><Skeleton width="65%" height={10} /></div>
                  </div>
                  <div className="certCard__footer certCard__footer--date">
                    <Skeleton width={14} height={14} borderRadius={3} />
                    <Skeleton width={72} height={11} borderRadius={4} />
                  </div>
                </Card>
              ))
            : masterCertError
            ? <div style={{ gridColumn: '1 / -1' }}><ErrorState compact /></div>
            : filteredCertificates.length === 0
            ? (
              <div style={{ gridColumn: '1 / -1' }} className="panel__emptyState">
                <div className="panel__emptyState__iconWrap">
                  <i className="pi pi-file panel__emptyState__icon" />
                </div>
                <div className="panel__emptyState__title">No certificates yet</div>
                <div className="panel__emptyState__sub">Click "Request certificate" to get started.</div>
              </div>
            )
            : pagedCertificates.map((c) => <CertificateCard key={c.id} certificate={c} />)}
        </div>
        {filteredCertificates.length > 5 && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
            <Paginator
              first={certPageFirst}
              rows={5}
              totalRecords={filteredCertificates.length}
              onPageChange={(e: { first: number }) => setCertPageFirst(e.first)}
              template="PrevPageLink PageLinks NextPageLink"
              className="requests-paginator"
            />
          </div>
        )}
        {/* Show AddCertificate form as a modal/dialog if showAddCertificate is true */}
        {showAddCertificate && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.25)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AddCertificate visible={showAddCertificate} onHide={() => setShowAddCertificate(false)} />
          </div>
        )}
      </div>

      <Card className="pageCard">
        <TabView className="dashboardTabView">
          <TabPanel header="Requests">
            {insuredId && <InsuredRequestsPanel insuredId={insuredId} />}
          </TabPanel>
          <TabPanel header="Sent Certificates">
            {insuredId && (
              <SentCertificatesPanel
                apiUrl={`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/sent-certificates`}
                token={localStorage.getItem('igtoken') || ''}
                insuredDatabaseId={insuredId}
              />
            )}
          </TabPanel>
          <TabPanel header={
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Activity
              {unreadCount > 0 && (
                <span style={{ background: '#ef4444', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 700, lineHeight: '16px', minWidth: 18, textAlign: 'center', display: 'inline-block' }}>
                  {unreadCount}
                </span>
              )}
            </span>
          }>
            <RecentActivityPanel />
          </TabPanel>
        </TabView>
      </Card>
    </div>
  );
}
